// NewsTalk (c) Copyright 2023 by Qwixotic
// 
// File:   Comment.js
// Author: Percipient Reverence
// Date:   6-Jun-2023
//
// Description:
//
//   For composing, editing and saving comments.

// React imports
import React, {useEffect, useState, useRef, useMemo} from 'react'
import        { useParams, useOutletContext }        from 'react-router-dom'

// MUI imports
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'

// TinyMCE imports
import { Editor } from '@tinymce/tinymce-react';

// Local imports
import { ArticleHeader, CommentItem, MessageBox } from './lib/Components'
import { myFetch, myPost }                        from './lib/Util'

// ```` 12/12/2023 - Work on this soon: when I'm on the comment composition page if I sign out then
// it leaves that page up.  If I try to submit the message it hangs.  Have to fix all this.  Perhaps
// I can have the sign out process return me to the article page.  I'm going to have to include a
// check for it I'm on the comment composition page or if I'm logged into a privileged account and
// am on an admin-only page.

export const CommentReplyEP = (props) => {
  const {signinCredentials} = useOutletContext()
  const editorRefNewComment = useRef(null)

  let [Article, ArticleSet] = useState({})

  const Params = useParams()

  // Check for errors - people can modify the parameter value in URLs

  const errorText = useRef('')
  let SID = Params.SID ? parseInt(Params.SID) : 1
  let AID = Params.AID ? parseInt(Params.AID) : 1
  let replyToMID = Params.MID ? parseInt(Params.MID) : 0
  if (!signinCredentials || signinCredentials.idnum < 0) {
    errorText.current = 'Must be signed in to post comments'
  } else if (SID < 1) {
    errorText.current = 'Invalid section id'
  } else if (AID < 1) {
    errorText.current = 'Invalid article id'
  } else if (replyToMID < 0) {
    errorText.current = 'Invalid reply-to message id'
  }

  // Fetch all article data including the comment we're replying to, if any.
  if (Object.keys(Article).length === 0) {
    console.log('CREP a-1: fetching article info for making reply')
    myFetch('/nt/Article/Reply/' + SID + '/' + AID + '/' + replyToMID)
      .then((res) => res.json())
      .then(ArticleSet)
    if (Article.status === 'Error') {
      errorText.current = Article.message
    }
  }

  const [triggerSubmitComment, setTriggerSubmitComment] = useState(false)
  const body = useMemo(() => {
    return (
      {AID:          Article.AID,
       replyToMID:   replyToMID,
       MBRID:        signinCredentials.idnum
      }
    )
  }, [Article.AID, replyToMID, signinCredentials.idnum])
  useEffect(() => {
    console.log('CREP a-1+1: Checking errorText.current: ' + errorText.current);
    if (!errorText.current) {
      console.log('CREP a-1+2: Checking triggerSubmitComment: ' + triggerSubmitComment);
      if (triggerSubmitComment) {
        console.log('CREP a: Submission of comment triggered');
        setTriggerSubmitComment(false)
        body.message = editorRefNewComment.current ? editorRefNewComment.current.getContent() : null
        body.title = document.getElementById('CommentTitle').value
        myPost('/nt/Comment', body)
          .then(response => response.json())
          .then(data => {
            if (true) {
              window.location = `/Article/MID/${SID}/${data.MID}`
            } else {
              console.log('CREP b: Change of window location disabled')
            }
          })
        if (Article.status === 'Error') {
          errorText.current = Article.message
        }
      }
    } 
  }, [Article, triggerSubmitComment, AID, SID, body])

  // The <TextField> component's assignment to sx is explained in my NodeReactHelp.txt document
  // under MUI Styles.

  let title = ''
  if (replyToMID > 0) {
    if (Article.replyToComment && Article.replyToComment.Title) {
      title = Article.replyToComment.Title.substr(0, 4) === 'Re: '
        ? Article.replyToComment.Title
        : 'Re: ' + Article.replyToComment.Title
    }
  }

  console.log('Article: ' + JSON.stringify(Article))

  return (
    (errorText.current || !Article || !Article.Section || !Article.MediaOutlet)
      ? (errorText.current
         ? <MessageBox color='red'>
             {errorText.current}
           </MessageBox>
         : (
           <>
             Loading...
           </>
         )
        )
      : (
        <>
          <ArticleHeader Article={Article}/>
          <Grid container direction='row' justifyContent='center'>
            <Grid item sx={{width: '90%', marginBottom: '5px'}}>
              <TextField id='CommentTitle' label='Optional Title' variant='outlined' fullWidth size='small' defaultValue={title}
                         sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px',
                                                           '& fieldset': {borderWidth: '2px', borderColor: 'rgba(0, 0, 0, .07)'}}}}/>
            </Grid>
            <Grid item sx={{width: '90%'}}>
              <Editor
                tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                onInit={(evt, editor) => {editorRefNewComment.current = editor; editor.focus()}}
                initialValue_orig={'<p>Initial content</p>'}
                initialValue={null}
                justifyContents='center'
                init={{
                  promotion: false,
                  branding: false,
                  placeholder: 'Enter comment here...',
                  height: 500,
                  min_height: 500,
                  autoresize_bottom_margin: 20,
                  width: '100%',
                  horizontalAlign: 'center',
                  plugins: [
                    'advlist', 'autolink', 'autoresize', 'lists', 'link', 'image', 'charmap',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks fontfamily | blockquote | ' +
                    'bold italic underline strikethrough forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | table | image | ' +
                    'removeformat | help',
                  content_style_skip: 'body { font-family:Roboto,Helvetica,Arial,sans-serif; font-size:19px; }',
                  content_css: '/TinyMCE.css',
                  save_onsavecallback: () => {
                  }
                }}
              />
            </Grid>
          </Grid>
          <Grid item sx={{textAlign: 'center', marginTop: '10px'}}>
            <Button variant='contained' size='small' disabled={triggerSubmitComment}
                    onClick={() => {console.log('Submit button clicked'); setTriggerSubmitComment(true)}}>
              Submit
            </Button>
          </Grid>
          {replyToMID > 0
           ? <Grid container direction='row' justifyContent='center' sx={{marginTop: '.6rem', fontFamily: 'Roboto'}}>
               Comment Being Responded To
               <Grid item sx={{width: '90%', marginTop: '.2rem',
                               borderStyle: 'solid', borderColor: '#ddd', borderWidth: '2px', borderRadius: '10px'}}>
                 <CommentItem Comment={Article.replyToComment} Comments={Article.Comments} TotalMsgs={Article.NumPosts}
                              Section={Article.Section}/>
               </Grid>
             </Grid>
           : null
          }
        </>
      )
  )
}
